import React, { useMemo } from 'react';
import { ArrowLeft, Star, Palmtree } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';

import ChatView from './ChatView';
import TasksView from './TasksView';
import NotepadView from './NotepadView';
import Avatar from './Avatar';
import Breadcrumbs from './Breadcrumbs';
import MediaPlayer from './MediaPlayer';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectDataContext';
import { useEmulator } from '../firebase';

const MobileProjectView = ({
  state,
  dispatch,
  handleSendMessage,
  handleSaveEditMessage,
  handleToggleReaction,
  handleDeleteMessage,
  handleScrollToMessage,
  handleStartDM,
  getDmUser,
  getProjectPath,
  getRootProject,
  handleSaveEditTask,
  handleContextMenu,
  deleteTask,
  moveToRoot,
  toggleTask,
  toggleTaskStar,
  handleAddTask,
  handleMoveTaskPriority,
  handleAddPersonalTask,
  linkify,
  personalTasks,
  tasks,
  activeChannel: propActiveChannel,
  isDM,
  activeColorHex,
  messages,
  notepadRef,
  savedSelection,
  noteTimeoutRef,
  isLocalEditRef,
  taskInputRef,
  taskEditRef,
  chatInputRef,
  handleLoadMore,
  messageLimit,
  toggleProjectStar,
  sensors,
  handleDragStart,
  handleDragEnd,
  handleGoHome,
  handleGoTikiBar,
  handleGoChat,
  handleSaveNote,
  handleBackgroundUpload,
  activeModule: propActiveModule,
  user: propUser,
  username,
  userAvatar,
  isTouch,
  b2Token,
  overrideId,
  overrideModule,
  navigate: propNavigate
}) => {
  const { user: authUser } = useAuth();
  const user = propUser || authUser;
  const { channels, allUsers, allTasks } = useProject();
  const { id: paramProjectId, module: paramModule } = useParams();
  const projectId = overrideId || paramProjectId;
  const currentModule = overrideModule || propActiveModule || paramModule || 'chat';
  const navigateBase = useNavigateWithParams();
  const navigate = propNavigate || navigateBase;

  // Internal activeChannel calculation
  const activeChannel = useMemo(() => {
    if (propActiveChannel) return propActiveChannel;
    const found = (channels || []).find((c) => c.id === projectId);
    if (found) {
      // Participation Check
      if (found.id !== 'general_chat_global' && !found.id.startsWith('personal-')) {
        const participants = found.participants || [found.createdBy];
        const uid = user?.uid || 'test-uid-123';
        if (!participants.includes(uid)) return null;
      }
      return found;
    }
    if (projectId === 'general_chat_global')
      return { id: 'general_chat_global', name: 'Tiki Bar', type: 'chat' };
    if (projectId && projectId.startsWith('personal-')) {
      const isNote = projectId === 'personal-note' || currentModule === 'notes';
      return {
        id: projectId,
        name: isNote ? 'Personal Notepad' : 'Personal Tasks',
        type: 'personal',
      };
    }
    return null;
  }, [propActiveChannel, channels, projectId, currentModule]);

  React.useEffect(() => {
    if (activeChannel?.id) {
      dispatch({ type: 'SET_ACTIVE_CHANNEL', payload: activeChannel.id });
    }
    // MANDATE: DMs are chat only.
    if (activeChannel?.type === 'dm' && currentModule !== 'chat') {
      navigate(`/p/${activeChannel.id}/chat`, { replace: true });
    }
  }, [activeChannel?.id, activeChannel?.type, currentModule, dispatch, navigate]);

  if (!activeChannel)
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-white gap-4">
        {useEmulator ? (
          <span className="text-5xl animate-pulse">🌵</span>
        ) : (
          <Palmtree size={48} className="text-green-600 animate-pulse" strokeWidth={2.5} />
        )}
        <h1 className="font-black text-2xl text-zinc-900 tracking-tighter">NYTM</h1>
      </div>
    );

  const {
    chatChannelId,
    activeChannelId,
    reactionFilter,
    editingMessageId,
    chatInputHtml,
    appId,
    currentView,
    editingTaskId,
    hiddenCompletedHeaderIds,
    selectedHeaderId,
    taskFontSize,
    collapsedHeaderIds,
    showOnlyStarredTasks,
    isSpellCheckEnabled,
    isSavingNote,
    isTableActive,
    isTableMenuOpen,
    activeId,
    deletingTaskId,
    editTaskText,
    editTaskDueDate,
    editTaskVenueName,
    editTaskAddress,
    editTaskAddressLat,
    editTaskAddressLng,
    newTask,
    newTaskDate,
    newTaskDueDate,
    newTaskIsHeader,
    isStarredProjectsOpen,
    contextMenu,
    events,
    activeItem,
  } = state;

  const renderContent = () => {
    const activeModule =
      currentModule ||
      (projectId.startsWith('personal-note-')
        ? 'notes'
        : projectId.startsWith('personal-')
          ? 'tasks'
          : 'chat');
    switch (activeModule) {
      case 'tasks': {
        const rootProject = getRootProject(activeChannel.id, channels);
        const participants = rootProject
          ? rootProject.participants || [rootProject.createdBy]
          : [user?.uid];
        return (
          <TasksView
            {...{
              currentView: activeChannel?.type === 'personal' ? 'home' : currentView,
              username,
              hiddenCompletedHeaderIds,
              activeColorHex,
              dispatch,
              personalTasks,
              tasks,
              editingTaskId,
              taskEditRef,
              editTaskText,
              handleSaveEditTask,
              editTaskDueDate,
              editTaskVenueName,
              editTaskAddress,
              editTaskAddressLat,
              editTaskAddressLng,
              handleContextMenu,
              linkify,
              deletingTaskId,
              deleteTask,
              moveToRoot,
              toggleTask,
              toggleTaskStar,
              handleMoveTaskPriority,
              activeId,
              allUsers,
              participants,
              newTask,
              newTaskDate,
              newTaskDueDate,
              newTaskIsHeader,
              taskInputRef,
              handleAddTask,
              handleAddPersonalTask,
              isStarredProjectsOpen,
              channels,
              getRootProject,
              getProjectPath,
              activeChannelId: activeChannel.id,
              user,
              selectedHeaderId,
              taskFontSize,
              collapsedHeaderIds,
              showOnlyStarredTasks,
              activeItem,
              isTouch,
            }}
          />
        );
      }
      case 'notes':
        return (
          <NotepadView
            key={activeChannel.id}
            {...{
              currentView,
              activeColorHex,
              notepadRef,
              isSpellCheckEnabled,
              isSavingNote,
              dispatch,
              contextMenu,
              chatInputRef: null,
              savedSelection,
              isTableActive,
              isTableMenuOpen,
              user,
              username,
              activeChannelId: activeChannel.id,
              noteTimeoutRef,
              isLocalEditRef,
              handleSaveNote,
              activeUploads: state.activeUploads,
              handleBackgroundUpload: handleBackgroundUpload,
            }}
          />
        );
      case 'chat':
      default: {
        const isTikiBar = projectId === 'general_chat_global';
        const isDMChannel = activeChannel.type === 'dm';
        return (
          <ChatView
            isFullWidth={true}
            isTouch={isTouch}
            customHeader={
              <div className="flex items-center gap-2 overflow-hidden flex-1">
                <button
                  onClick={() => (isTikiBar || isDMChannel ? handleGoChat() : handleGoHome())}
                  className="p-1 text-zinc-500 active:scale-90 transition-transform"
                >
                  <ArrowLeft size={24} strokeWidth={2.5} />
                </button>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {isDM && getDmUser(activeChannel) && (
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Avatar
                        src={getDmUser(activeChannel).avatar}
                        size={24}
                        className="flex-shrink-0"
                      />
                      <h1 className="font-bold text-zinc-900 text-sm truncate">
                        {getDmUser(activeChannel).name}
                      </h1>
                    </div>
                  )}
                  {!isDM && (
                    <h1 className="font-bold text-zinc-900 text-sm truncate flex-1">
                      {isTikiBar ? 'Tiki Bar' : activeChannel.name}
                    </h1>
                  )}
                  {!isDM && !isTikiBar && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleProjectStar(activeChannel);
                      }}
                      className="p-2 active:scale-90 transition-transform"
                    >
                      <Star
                        size={18}
                        className={
                          activeChannel.isStarred ? 'text-yellow-500 fill-current' : 'text-zinc-300'
                        }
                      />
                    </button>
                  )}
                </div>
              </div>
            }
            b2Token={b2Token}
            user={user}
            channels={channels}
            activeChannelId={activeChannel.id}
            currentView={currentView}
            dispatch={dispatch}
            allUsers={allUsers}
            messages={messages}
            editingMessageId={editingMessageId}
            chatChannelId={chatChannelId}
            isChatOpen={true}
            reactionFilter={reactionFilter}
            activeColorHex={activeColorHex}
            chatInputHtml={chatInputHtml || ''}
            appId={appId}
            username={username}
            userAvatar={userAvatar}
            handleSendMessage={handleSendMessage}
            handleSaveEditMessage={handleSaveEditMessage}
            handleToggleReaction={handleToggleReaction}
            handleDeleteMessage={handleDeleteMessage}
            handleScrollToMessage={handleScrollToMessage}
            handleStartDM={handleStartDM}
            getDmUser={getDmUser}
            handleLoadMore={handleLoadMore}
            messageLimit={messageLimit}
          />
        );
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden relative font-sans">
      {currentModule && currentModule !== 'chat' && (
        <header
          className="h-14 px-4 border-b border-zinc-100 flex items-center gap-3 absolute top-0 left-0 right-0 z-30 bg-white"
          style={{ backgroundColor: activeColorHex + '1A' }}
        >
          <button
            onClick={() => handleGoHome()}
            className="p-1 text-zinc-500 active:scale-90 transition-transform"
          >
            <ArrowLeft size={24} strokeWidth={2.5} />
          </button>
          <div className="flex flex-col min-w-0 flex-1">
            <Breadcrumbs
              path={getProjectPath(activeChannel.id, channels)}
              onNavigate={navigate}
              currentModule={currentModule}
              isMobile={true}
              fallbackName={projectId === 'general_chat_global' ? 'Tiki Bar' : activeChannel.name}
              fallbackId={projectId}
            />{' '}
          </div>
          {!isDM && projectId !== 'general_chat_global' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleProjectStar(activeChannel);
              }}
              className="p-2 active:scale-90 transition-transform"
            >
              <Star
                size={20}
                className={
                  activeChannel.isStarred ? 'text-yellow-500 fill-current' : 'text-zinc-300'
                }
              />
            </button>
          )}
        </header>
      )}

      <main
        className={`flex-1 overflow-hidden relative ${currentModule && currentModule !== 'chat' ? 'mt-14' : ''}`}
      >
        {renderContent()}
      </main>

      {state.isMediaPlayerOpen && (
        <div className="h-1/4 min-h-[150px] max-h-[300px] border-t border-zinc-200 bg-white">
          <MediaPlayer
            channelId={activeChannel.id}
            isGlobal={false}
            authUser={user}
            allUsers={allUsers}
            activeUploads={state.activeUploads}
            handleBackgroundUpload={handleBackgroundUpload}
          />
        </div>
      )}

    </div>
  );
};

export default MobileProjectView;
